import {createSlice, current} from '@reduxjs/toolkit';
import isArray from 'lodash.isarray';
import {createSelector} from 'reselect';
import {STRIP_LABELS} from 'src/components/criteria-strip/constants';
import {
    selectAccountStatuses,
    selectLicenseCrmModules,
    selectLicenseTypes,
    selectUserStatuses,
    selectUserTypes,
} from 'src/hooks/dictionaries/services/dictionary-slice';
import {CrmType as CrmTypes} from 'src/openapi/admin/models/CrmType';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {LocalStorage} from '@pb/kit-util-client-storage';

import type {PayloadAction} from '@reduxjs/toolkit';
import type {AccountManagerSearchResult} from 'src/openapi/admin/models/AccountManagerSearchResult';
import type {AccountSearchCriteria} from 'src/openapi/admin/models/AccountSearchCriteria';
import type {CrmSearchId} from 'src/openapi/admin/models/CrmSearchId';
import type {UserSearchCriteria} from 'src/openapi/admin/models/UserSearchCriteria';
import type {AppState} from 'src/store/typedef';
import type {Pill} from 'src/typedef';

export type CriteriaValue = number | boolean | string;
export type SearchCriteriaType = AccountSearchCriteria & UserSearchCriteria;
export type CriteriaKey = keyof SearchCriteriaType;

export type RenderedPill<T> = {
    value?: T,
    visibleValue: string,
};

export type SearchCriteria = {
    accountIds?: Array<Pill<number>>,
    accountNames?: Array<Pill<string>>,
    accountManagerId?: Pill<number>,
    licenseType?: Array<Pill<string>>,
    entityType?: Pill<string>,
    accountStatuses?: Array<Pill<string>>,
    userIds?: Array<Pill<number>>,
    firstName?: Pill<string>,
    lastName?: Pill<string>,
    userEmails?: Array<Pill<string>>,
    userStatuses?: Array<Pill<string>>,
    userTypes?: Array<Pill<string>>,
    rtsId?: Pill<string>,
    sfdcId?: Pill<string>,
    crmIntegrations?: Array<Pill<string>>,
    pbSalesforceIds?: Array<Pill<string>>,
};


type StripItem<T> = {
    propName: CriteriaKey,
    label?: string,
    pill: Array<Pill<T>> | Pill<T>,
};

export type CriteriaTree = Array<StripItem<CriteriaValue>>;

export type Search = {
    searchId: string,
    criteria: CriteriaTree | null,
    accountManager: AccountManagerSearchResult | null,
};

const SEARCH_HISTORY = 'SEARCH_HISTORY';
const MAX_SEARCH_HISTORY_ITEMS = 5;

export const initialState: Search = {
    searchId: '',
    criteria: null,
    accountManager: null,
};

const filterInvalidPills = (criteria: CriteriaTree) => criteria
    .filter(({pill}) => (!isArray(pill) ? !pill?.invalid : !!pill.filter(({invalid}) => !invalid).length))
    .map(item => ({...item, pill: isArray(item.pill) ? item.pill.filter(({invalid}) => !invalid) : item.pill}));

export const criteriaStripSlice = createSlice({
    name: 'criteriaStrip',
    initialState,
    reducers: {
        setSearchId: (state, action: PayloadAction<string>) => {
            const searchId = action.payload;
            const searchHistory = (LocalStorage.get(SEARCH_HISTORY) as Search[]) || [];

            const item: Search = {searchId, criteria: [], accountManager: null};
            LocalStorage.set(SEARCH_HISTORY, [item, ...searchHistory].slice(0, MAX_SEARCH_HISTORY_ITEMS));
            state.searchId = searchId;
        },
        getCriteria: (state, action: PayloadAction<string>) => {
            const search: Search | undefined = (LocalStorage.get(SEARCH_HISTORY) as Search[] | null)?.find(
                (item: Search) => item.searchId === action.payload,
            );

            if (search) {
                state.searchId = search.searchId;
                state.criteria = search.criteria;
                state.accountManager = search.accountManager;
            }
        },
        setPill: (state, action: PayloadAction<StripItem<CriteriaValue>>) => {
            const {pill, propName, label = STRIP_LABELS[propName as string]} = action.payload;
            const searchId = current(state).searchId;

            if (state.criteria) {
                const pillId = state.criteria.findIndex(element => element.propName === propName);
                let currentTree = [...current(state.criteria)];
                const currentValue = currentTree[pillId]?.pill;

                if (pillId > -1) {
                    currentTree[pillId] = {
                        propName,
                        label,
                        pill: Array.isArray(pill) && Array.isArray(currentValue)
                            ? [
                                    ...currentValue,
                                    ...pill.filter(({value}) => !currentValue.map(item => item.value).includes(value)),
                                ] : pill,
                    };
                } else {
                    currentTree = [...currentTree, {pill, propName, label}];
                }

                state.criteria = currentTree;
                const searchHistory = (LocalStorage.get(SEARCH_HISTORY) as Search[]) || [];
                const item = searchHistory.find((searchItem: Search) => searchItem.searchId === searchId);
                LocalStorage.set(SEARCH_HISTORY, [
                    ...searchHistory.filter((searchItem: Search) => !isDeepEqual({prev: searchItem, current: item})),
                    {...item, criteria: currentTree},
                ]);
            }
        },
        replacePill: (state, action: PayloadAction<StripItem<CriteriaValue>>) => {
            const {pill, propName, label = STRIP_LABELS[propName as string]} = action.payload;
            const searchId = current(state).searchId;

            if (state.criteria) {
                const pillId = state.criteria.findIndex(element => element.propName === propName);
                let currentTree = [...current(state.criteria)];

                if (pillId > -1) {
                    currentTree[pillId] = {propName, label, pill};
                } else {
                    currentTree = [...currentTree, {pill, propName, label}];
                }

                state.criteria = currentTree;
                const searchHistory = (LocalStorage.get(SEARCH_HISTORY) as Search[]) || [];
                const item = searchHistory.find((searchItem: Search) => searchItem.searchId === searchId);
                LocalStorage.set(SEARCH_HISTORY, [
                    ...searchHistory.filter((searchItem: Search) => !isDeepEqual({prev: searchItem, current: item})),
                    {...item, criteria: currentTree},
                ]);
            }
        },
        removePill: (state, action: PayloadAction<{propName: CriteriaKey, forRemove?: CriteriaValue}>) => {
            const {propName, forRemove} = action.payload;
            const searchId = current(state).searchId;

            if (state.criteria) {
                let currentTree = [...current(state.criteria)];

                if (forRemove) {
                    const parentId = currentTree.findIndex(pill => pill.propName === propName);
                    let children = currentTree[parentId]?.pill;

                    if (Array.isArray(children)) {
                        children = children.filter(({value}) => value !== forRemove);

                        const removeAdditionalCriteria = [
                            propName === 'licenseType' &&
                            children.length === 1 &&
                            children[0].value === 'includeInactiveLicenses',

                            propName === 'crmIntegrations' &&
                            children.length === 1 &&
                            children[0].value === 'includeInactiveCrm',
                        ].some(Boolean);

                        if (children.length && !removeAdditionalCriteria) {
                            currentTree[parentId] = {
                                propName,
                                label: currentTree[parentId].label,
                                pill: children,
                            };
                        } else {
                            currentTree = currentTree.filter(pill => pill.propName !== propName);
                        }
                    }
                } else {
                    if (propName === 'accountManagerId') {
                        state.accountManager = null;
                    }
                    currentTree = currentTree.filter(pill => pill.propName !== propName);
                }

                state.criteria = currentTree;

                const searchHistory = (LocalStorage.get(SEARCH_HISTORY) as Search[]) || [];
                const item = searchHistory.find((searchItem: Search) => searchItem.searchId === searchId);
                LocalStorage.set(SEARCH_HISTORY, [
                    ...searchHistory.filter((searchItem: Search) => !isDeepEqual({prev: searchItem, current: item})),
                    {...item, criteria: currentTree, accountManager: state.accountManager},
                ]);
            }
        },
        clearStrip: state => {
            const searchId = current(state).searchId;
            state.criteria = [];
            state.accountManager = null;

            const searchHistory = (LocalStorage.get(SEARCH_HISTORY) as Search[]) || [];
            const item = searchHistory.find((searchItem: Search) => searchItem.searchId === searchId);
            LocalStorage.set(SEARCH_HISTORY, [
                ...searchHistory.filter((searchItem: Search) => !isDeepEqual({prev: searchItem, current: item})),
                {...item, criteria: [], accountManager: null},
            ]);
        },
        setAccountManager: (state, action: PayloadAction<AccountManagerSearchResult>) => {
            const searchId = current(state).searchId;
            state.accountManager = action.payload;

            const searchHistory = (LocalStorage.get(SEARCH_HISTORY) as Search[]) || [];
            const item = searchHistory.find((searchItem: Search) => searchItem.searchId === searchId);

            LocalStorage.set(SEARCH_HISTORY, [
                ...searchHistory.filter((searchItem: Search) => !isDeepEqual({prev: searchItem, current: item})),
                {...item, accountManager: action.payload},
            ]);
        },
    },
});

export const selectAccountManager = (state: AppState): AccountManagerSearchResult | null =>
    state.criteriaStrip.accountManager;

export const selectCriteriaStrip = (state: AppState): CriteriaTree | null =>
    state.criteriaStrip.criteria;

export const selectSearchCriteria = createSelector<
    AppState,
    Array<StripItem<CriteriaValue>> | null,
    SearchCriteria | null
>(
    [
        selectCriteriaStrip,
    ],
    (strip: Array<StripItem<CriteriaValue>> | null) => (strip ? Object.fromEntries(strip
        .map(({propName, pill}) => [propName, pill]),
    ) : null),
);

export const selectSearchRequest = createSelector<
    AppState,
    Array<StripItem<CriteriaValue>> | null,
    SearchCriteriaType | null
>(
    [selectCriteriaStrip],
    (strip: Array<StripItem<CriteriaValue>> | null) => {
        if (!strip) {
            return null;
        }

        const rawRequest = Object.fromEntries(filterInvalidPills(strip)
            .map(({propName, pill}) => [propName, isArray(pill)
                ? pill.map(({value}) => value)
                : pill.value]));

        const {
            licenseType,
            crmIntegrations,
            ...otherCriteria
        } = rawRequest;

        const preparedCriteria = otherCriteria as AccountSearchCriteria;

        if (licenseType && isArray(licenseType)) {
            preparedCriteria.licenseType = (licenseType as string[])
                .find((item: string) => item !== 'includeInactiveLicenses');

            const includeInactiveLicenses = (licenseType as string[])
                .find((item: string) => item === 'includeInactiveLicenses');

            if (includeInactiveLicenses) {
                preparedCriteria.includeInactiveLicenses = true;
            }
        }

        if (crmIntegrations && isArray(crmIntegrations)) {
            preparedCriteria.crmIntegrations = (crmIntegrations as CrmSearchId[])
                .filter((item: string) => item !== 'includeInactiveCrm');

            const includeInactiveCrm = (crmIntegrations as CrmSearchId[])
                .find((item: string) => item === 'includeInactiveCrm');

            if (includeInactiveCrm) {
                preparedCriteria.includeInactiveCrm = true;
            }
        }

        return preparedCriteria;
    },
);

export const selectCriteriaStripView = createSelector(
    [
        selectCriteriaStrip,
        selectAccountManager,
        selectAccountStatuses,
        selectUserStatuses,
        selectLicenseTypes,
        selectUserTypes,
        selectLicenseCrmModules,
    ],
    // eslint-disable-next-line max-params
    (strip, accountManager, accountStatuses, userStatuses, licenseTypes, userTypes, crmIntegrations) => (
        strip ? filterInvalidPills(strip).map(({propName, label, pill}) => {
            let formattedPill: RenderedPill<CriteriaValue> | RenderedPill<CriteriaValue>[] = [];

            if (isArray(pill)) {
                switch (propName) {
                    case 'accountStatuses':
                        if (accountStatuses.length === pill.length) {
                            formattedPill = {visibleValue: 'All Account Statuses'};
                        } else {
                            formattedPill = pill.map(({value}) => ({
                                value,
                                visibleValue: accountStatuses.find(({code}) => code === value)?.description || '',
                            }));
                        }
                        break;
                    case 'crmIntegrations':
                        const availableCRMs = crmIntegrations.filter(({code}) => code !== CrmTypes.PITCHBOOK);
                        const crmFilteredPills = pill
                            .filter(({value}) => value !== 'includeInactiveCrm');

                        if (availableCRMs.length === crmFilteredPills.length) {
                            const includeInactiveCrm = pill.find(({value}) => value === 'includeInactiveCrm');

                            formattedPill = [
                                {visibleValue: 'All CRM Integrations'},
                                ...includeInactiveCrm
                                    ? [{value: includeInactiveCrm?.value, visibleValue: 'Include inactive licenses'}]
                                    : [],
                            ];
                        } else {
                            formattedPill = pill.map(({value}) => ({
                                value,
                                visibleValue: value !== 'includeInactiveCrm'
                                    ? crmIntegrations.find(({code}) => code === value)?.description || ''
                                    : 'Include inactive licenses',
                            }));
                        }
                        break;
                    case 'userStatuses':
                        if (userStatuses.length === pill.length) {
                            formattedPill = {visibleValue: 'All User Statuses'};
                        } else {
                            formattedPill = pill.map(({value}) => ({
                                value,
                                visibleValue: userStatuses.find(({code}) => code === value)?.description || '',
                            }));
                        }
                        break;
                    case 'userTypes':
                        if (userTypes.length === pill.length) {
                            formattedPill = {visibleValue: 'All User Types'};
                        } else {
                            formattedPill = pill.map(({value}) => ({
                                value,
                                visibleValue: userTypes.find(({code}) => code === value)?.description || '',
                            }));
                        }
                        break;
                    case 'userIds':
                    case 'accountNames':
                    case 'userEmails':
                    case 'accountIds':
                    case 'pbSalesforceIds':
                        formattedPill = pill.map(({value}) => ({
                            value,
                            visibleValue: value.toString(),
                        }));
                        break;
                    case 'licenseType':
                        formattedPill = pill.map(({value}) => ({
                            value,
                            visibleValue: value !== 'includeInactiveLicenses'
                                ? licenseTypes.find(({code}) => code === value)?.description || ''
                                : 'Include inactive licenses',
                        }));
                        break;
                }
            } else {
                switch (propName) {
                    case 'firstName':
                    case 'lastName':
                    case 'rtsId':
                    case 'entityType':
                    case 'sfdcId':
                        formattedPill = {visibleValue: pill.value.toString()};
                        break;
                    case 'accountManagerId':
                        formattedPill = {visibleValue: accountManager?.fullName || accountManager?.email || ''};
                        break;
                    case 'includeInactiveLicenses':
                        formattedPill = {visibleValue: 'Include inactive licenses'};
                        break;
                }
            }
            return ({propName, label, pill: formattedPill});
        }) : null),
);

export const {
    setSearchId,
    getCriteria,
    setPill,
    replacePill,
    removePill,
    clearStrip,
    setAccountManager,
} = criteriaStripSlice.actions;
